/**
 * Footer line under a forecast chart: per-series forecast figures plus the
 * combined total (Fan, 2026-08-31: "On all dashboards and pages with
 * forecasts, please give the total for them (FR + UK together)").
 *
 * Deliberately mirrors the existing "YTD ...: FR / UK / Combined" footers so
 * the two lines read as a pair: actuals to date on top, contracted forward
 * bookings underneath. Totals come from the chart's own data array
 * (lib/forecastTotals.js), so the footer can never disagree with the bars.
 *
 * mode='avg' is for rate/percentage charts (occupancy, ADR, RevPAR) where a
 * sum is meaningless -- it shows the forecast-window average per series and
 * no combined figure unless an explicit combined series is passed in.
 */
import React from 'react';
import { computeForecastTotals, computeForecastAverages, forecastWindowLabel } from '../../lib/forecastTotals';
import { formatCompact } from '../../lib/chartFormatters';
import { flagFor } from '../../lib/jurisdictionFlags';

export const COMBINED_COLOR = '#111827';

function Swatch({ color }) {
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full"
      style={{ backgroundColor: color, opacity: 0.55, border: `1px dashed ${color}` }}
    />
  );
}

/**
 * @param {object} props
 * @param {Array} props.data - the chart data array, as rendered
 * @param {Array<{key: string, label: string, color: string}>} props.series
 * @param {string} [props.currency='GBP'] - the chart's display currency
 * @param {string} [props.label='Forecast'] - metric name, e.g. 'Booking Volume'
 * @param {'sum'|'avg'} [props.mode='sum']
 * @param {boolean} [props.showCombined=true]
 * @param {function} [props.format] - (value) => string; defaults to formatCompact
 * @param {boolean} [props.includeCurrentMonthRemainder=true] - false when the
 *   page's YTD already includes the whole current month
 */
export function ForecastTotalsFooter({
  data,
  series,
  currency = 'GBP',
  label = 'Forecast',
  mode = 'sum',
  showCombined = true,
  format,
  includeCurrentMonthRemainder = true,
}) {
  const keys = (series || []).map((s) => s.key);
  const result = mode === 'avg'
    ? computeForecastAverages(data, keys)
    : computeForecastTotals(data, keys, { includePartial: includeCurrentMonthRemainder });

  if (!result.hasForecast) return null;

  const fmt = format || ((v) => formatCompact(v, currency));
  const window = forecastWindowLabel(result.months);
  const combined = mode === 'avg' ? null : result.total;

  return (
    <div className="mt-1 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
      <span>
        Forecast{window ? ` (${window})` : ''} {label}:
      </span>
      {(series || []).map((s) => {
        const value = result.byKey[s.key];
        if (value == null) return null;
        const flag = flagFor(s.label);
        return (
          <span key={s.key} className="inline-flex items-center gap-1">
            <Swatch color={s.color} />
            <span>{flag ? `${flag} ${s.label}` : s.label}</span>
            <span className="font-mono font-semibold text-foreground">{fmt(value)}</span>
          </span>
        );
      })}
      {showCombined && combined != null && (
        <span className="inline-flex items-center gap-1">
          <Swatch color={COMBINED_COLOR} />
          <span>Combined</span>
          <span className="font-mono font-semibold text-foreground">{fmt(combined)}</span>
        </span>
      )}
    </div>
  );
}

export default ForecastTotalsFooter;
